import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { DiamondDetailModal } from '../../components/DiamondDetailModal';
import { getDiamondShapeImage } from '../../utils/diamondShapeImages';
import { exportToCSV, exportToJSON, prepareDiamondsForExport } from '../../utils/exportDiamonds';
import { apiClient } from '../../utils/apiClient';
import { activityTracker } from '../../services/activityTracker';
import { notify } from '../../utils/notifications';
import mockDataService from '../../services/mockDataService';
import { getCertificateUrl } from '../../utils/certificateUrls';
import { ShapeFilter, ColorFilter, ClarityFilter, CutQualityFilter, LabFilter } from '../../components/Diamond/Filters';
import { FiSliders, FiDownload, FiGrid, FiList, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { FaGavel } from 'react-icons/fa';
import CreateAuctionModal from '../../components/Auction/CreateAuctionModal';
import { GiCutDiamond } from 'react-icons/gi';
import { StatCard } from '../Atelier/components/DiamondStats';
import { PageContainer, PageTitle, Text, Button } from '../../components/PageStyles';
import { InventoryContainer, InventoryHeader, InventoryTitleGroup, InventoryIcon, TitleText, InventoryTitle, InventorySubtitle, InventoryStats, StatPill, StatPillValue, StatPillLabel, ViewToggle, ViewBtn, InventoryTableWrapper, InventoryTable, DiamondThumb, StockNumber, ShapeBadge, CaratValue, GradeCell, LabBadge, CertNumber, PriceValue, PriceTotal, StatusIndicator, StatusDot, ActionsCell, ActionBtn, InventoryFooter, Pagination, PaginationBtn, PaginationInfo, ShowingInfo, FeaturedBadge } from './DiamondInventory.styles';
import { SortableTh } from './FinancialHub/SharedComponents';
import styled from 'styled-components';
// Styled components
const FilterSection = styled.div `
  background: var(--bg-secondary);
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid var(--border-color);
  border-radius: 0;

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 20px;
  }
`;
// Filter layout styled components - Single column flow layout (matching Atelier design)
const FilterMainGrid = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const FilterPairRow = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;
const FilterFullWidth = styled.div `
  width: 100%;
`;
const FilterGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;
const RangeGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
const RangeInputGroup = styled.div `
  label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
  }
`;
const RangeInput = styled.input `
  width: 100%;
  padding: 12px 14px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
const FilterHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;
const FilterTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 !important;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: var(--accent-gold, #B8860B);
  }
`;
const FilterLabel = styled.label `
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;
const SearchBar = styled.div `
  display: flex;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
const SearchInput = styled.input `
  flex: 1;
  padding: 14px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
const FilterActions = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;
const ClearButton = styled.button `
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  padding: 12px 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    color: var(--accent-gold, #B8860B);
  }
`;
const ExportButton = styled.button `
  background: var(--accent-gold, #B8860B);
  color: #FFFFFF;
  border: 1px solid var(--accent-gold, #B8860B);
  padding: 12px 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: var(--accent-gold-dark, #8B6914);
    border-color: var(--accent-gold-dark, #8B6914);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const Select = styled.select `
  width: 100%;
  padding: 8px 12px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--border-color-hover);
  }

  option {
    background: var(--bg-primary);
    color: var(--text-primary);
  }
`;
const TableContainer = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  overflow: hidden;
`;
const Table = styled.table `
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid var(--border-color);
    white-space: nowrap;
  }

  th {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 12px;
    position: sticky;
    top: 0;
    z-index: 2;

    &:last-child {
      position: sticky;
      right: 0;
      background: var(--bg-tertiary);
      box-shadow: -4px 0 8px rgba(0, 0, 0, 0.1);
      z-index: 3;
    }
  }

  td {
    color: var(--text-secondary);

    &:last-child {
      position: sticky;
      right: 0;
      background: var(--bg-secondary);
      box-shadow: -4px 0 8px rgba(0, 0, 0, 0.1);
      z-index: 1;
    }
  }

  tr:hover {
    background: var(--bg-tertiary);

    td:last-child {
      background: var(--bg-tertiary);
    }
  }

  .diamond-image {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 0;
    border: 1px solid var(--border-color);
  }

  .price-cell {
    font-weight: 600;
    color: var(--text-primary);
  }

  .carat-cell {
    font-weight: 500;
  }

  .actions-cell {
    display: flex;
    gap: 8px;
  }
`;
const TableWrapper = styled.div `
  max-height: 600px;
  overflow-y: auto;
  overflow-x: auto;
  position: relative;

  /* Scroll indicator shadow on right edge */
  &::after {
    content: '';
    position: sticky;
    right: 0;
    top: 0;
    bottom: 0;
    width: 30px;
    background: linear-gradient(to left, var(--bg-secondary), transparent);
    pointer-events: none;
    z-index: 1;
  }

  /* Custom scrollbar for table */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;
// Price Range Slider
const PriceRangeContainer = styled.div `
  margin-bottom: 20px;
`;
const PriceSliderWrapper = styled.div `
  position: relative;
  margin: 20px 0;
`;
const PriceSlider = styled.input `
  -webkit-appearance: none;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  outline: none;
  border-radius: 0;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: #FFFFFF;
    cursor: pointer;
    border-radius: 0;
    border: 2px solid #000000;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.2);
      box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
    }
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: #FFFFFF;
    cursor: pointer;
    border-radius: 0;
    border: 2px solid #000000;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.2);
      box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
    }
  }
`;
const PriceLabels = styled.div `
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;
const FilterSectionTitle = styled.h4 `
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
  margin-top: 0;
`;
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;
// StatCard extracted - now using shared component from Atelier
const ActionButton = styled(Button) `
  padding: 6px 12px;
  font-size: 12px;
  min-width: auto;
`;
const InquiryToggleBar = styled.div `
  background: var(--bg-secondary);
  border: 2px solid #f59e0b;
  padding: 16px 24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const ToggleInfo = styled.div `
  flex: 1;

  .toggle-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--text-primary);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 4px;
  }

  .toggle-description {
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.5;
  }

  .toggle-count {
    font-size: 12px;
    color: #f59e0b;
    font-weight: 600;
    margin-top: 4px;
  }
`;
const ToggleButton = styled.button `
  padding: 12px 24px;
  background: ${props => props.$active ? '#f59e0b' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  border: 2px solid #f59e0b;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
`;
const ActiveFilterBanner = styled.div `
  background: rgba(184, 134, 11, 0.1);
  border: 2px solid var(--accent-gold);
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  .filter-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .label {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .value {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--accent-gold);
    }
  }

  .clear-btn {
    padding: 0.5rem 1rem;
    background: transparent;
    border: 1px solid var(--accent-gold);
    border-radius: 4px;
    color: var(--accent-gold);
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: var(--accent-gold);
      color: white;
    }
  }
`;
const DiamondInventory = ({ initialFilters, onClearFilters }) => {
    const authStore = useAuthStore();
    const [diamonds, setDiamonds] = useState([]);
    const [filteredDiamonds, setFilteredDiamonds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDiamond, setSelectedDiamond] = useState(null);
    const [auctionDiamond, setAuctionDiamond] = useState(null);
    // Ref for auto-scrolling to inventory table
    const inventoryContainerRef = useRef(null);
    // Special filter from dashboard
    const [dashboardFilter, setDashboardFilter] = useState(initialFilters || null);
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 100;
    // Filter states
    const [filters, setFilters] = useState({
        shape: '',
        color: '',
        clarity: '',
        cut: '',
        lab: '',
        minCarat: '',
        maxCarat: '',
        minPrice: '',
        maxPrice: '',
        status: '',
        search: ''
    });
    // Hide inquiry diamonds (price=0) by default
    const [hideInquiryDiamonds, setHideInquiryDiamonds] = useState(true);
    // Update dashboard filter when initialFilters prop changes and auto-scroll
    useEffect(() => {
        setDashboardFilter(initialFilters || null);
        // Auto-scroll to inventory table when filter is applied from dashboard
        if (initialFilters) {
            setTimeout(() => {
                const element = inventoryContainerRef.current;
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    const targetY = scrollTop + rect.top - 100;
                    window.scrollTo({
                        top: targetY,
                        behavior: 'smooth'
                    });
                }
            }, 300);
        }
    }, [initialFilters]);
    // Sort state
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const handleSort = (key) => {
        if (sortKey === key) {
            // Cycle through: asc -> desc -> null
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            }
            else if (sortDirection === 'desc') {
                setSortDirection(null);
                setSortKey(null);
            }
            else {
                setSortDirection('asc');
            }
        }
        else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };
    // Load diamonds from CosmosDB API
    useEffect(() => {
        loadInventory();
    }, []);
    const loadInventory = async () => {
        try {
            setIsLoading(true);
            // Check if mock mode is enabled
            if (mockDataService.isMockMode()) {
                console.log('📊 Using mock diamond inventory (150 diamonds)');
                const mockDiamonds = mockDataService.getDiamonds();
                setDiamonds(mockDiamonds);
                setFilteredDiamonds(mockDiamonds);
                setIsLoading(false);
                return;
            }
            console.log('📊 Loading diamond inventory from API...');
            // Build query parameters for API
            const queryParams = new URLSearchParams();
            queryParams.append('limit', '1000');
            queryParams.append('sortBy', 'priceUSD');
            queryParams.append('sortOrder', 'asc');
            const response = await apiClient.get(`diamonds-inventory?${queryParams}`);
            if (response.ok && response.data?.success) {
                console.log(`✅ Loaded ${response.data.data.diamonds.length} diamonds from CosmosDB`);
                setDiamonds(response.data.data.diamonds);
                setFilteredDiamonds(response.data.data.diamonds);
            }
            else {
                const errorMsg = response.error || response.data?.message || 'Failed to load inventory';
                console.error('❌ Error loading inventory:', errorMsg);
                setDiamonds([]);
                setFilteredDiamonds([]);
                notify.error('Load Failed', `Failed to load diamond inventory: ${errorMsg}`);
            }
        }
        catch (error) {
            console.error('❌ Unexpected error loading inventory:', error);
            setDiamonds([]);
            setFilteredDiamonds([]);
            notify.error('Load Failed', `Failed to load diamond inventory: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        finally {
            setIsLoading(false);
        }
    };
    // Apply filters
    useEffect(() => {
        let filtered = diamonds;
        // Shape filter
        if (filters.shape) {
            filtered = filtered.filter(d => d.shape.toLowerCase() === filters.shape.toLowerCase());
        }
        // Color filter
        if (filters.color) {
            filtered = filtered.filter(d => d.color === filters.color);
        }
        // Clarity filter
        if (filters.clarity) {
            filtered = filtered.filter(d => d.clarity === filters.clarity);
        }
        // Cut filter
        if (filters.cut) {
            filtered = filtered.filter(d => d.cut === filters.cut);
        }
        // Lab filter
        if (filters.lab) {
            filtered = filtered.filter(d => d.lab === filters.lab);
        }
        // Carat range
        if (filters.minCarat) {
            const minCarat = parseFloat(filters.minCarat);
            filtered = filtered.filter(d => d.caratWeight >= minCarat);
        }
        if (filters.maxCarat) {
            const maxCarat = parseFloat(filters.maxCarat);
            filtered = filtered.filter(d => d.caratWeight <= maxCarat);
        }
        // Price range
        if (filters.minPrice) {
            const minPrice = parseFloat(filters.minPrice);
            filtered = filtered.filter(d => (d.priceUSD || d.totalPrice || 0) >= minPrice);
        }
        if (filters.maxPrice) {
            const maxPrice = parseFloat(filters.maxPrice);
            filtered = filtered.filter(d => (d.priceUSD || d.totalPrice || 0) <= maxPrice);
        }
        // Status filter
        if (filters.status) {
            filtered = filtered.filter(d => d.status === filters.status);
        }
        // Hide inquiry diamonds filter (applied by default)
        if (hideInquiryDiamonds) {
            filtered = filtered.filter(d => d.status !== 'inquiry');
        }
        // Search filter
        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(d => d.shape.toLowerCase().includes(searchTerm) ||
                d.color.toLowerCase().includes(searchTerm) ||
                d.clarity.toLowerCase().includes(searchTerm) ||
                d.lab.toLowerCase().includes(searchTerm) ||
                d.id.toLowerCase().includes(searchTerm));
        }
        // Dashboard filter (from "View All" buttons)
        if (dashboardFilter) {
            const now = new Date();
            switch (dashboardFilter.filterType) {
                case 'slow-moving':
                    // Filter diamonds that have been in stock 90+ days
                    filtered = filtered.filter(d => {
                        if (!d.importedAt)
                            return false;
                        const importDate = new Date(d.importedAt);
                        const daysInStock = Math.floor((now.getTime() - importDate.getTime()) / (1000 * 60 * 60 * 24));
                        return daysInStock >= (dashboardFilter.minDaysInStock || 90);
                    });
                    break;
                case 'high-value':
                    // Filter diamonds with price >= minPrice (default $50K)
                    filtered = filtered.filter(d => {
                        const price = d.priceUSD || d.totalPrice || 0;
                        return price >= (dashboardFilter.minPrice || 50000);
                    });
                    break;
                case 'low-margin':
                    // Filter diamonds with low margin (< 20%)
                    // Need costPrice to calculate margin
                    filtered = filtered.filter(d => {
                        if (!d.costPrice || d.costPrice === 0)
                            return false;
                        const price = d.priceUSD || d.totalPrice || 0;
                        const margin = ((price - d.costPrice) / d.costPrice) * 100;
                        return margin < 20;
                    });
                    break;
                case 'missing-cert':
                    // Filter diamonds without certificates
                    filtered = filtered.filter(d => !d.certified || !d.certificateNumber);
                    break;
            }
        }
        setFilteredDiamonds(filtered);
        setCurrentPage(1); // Reset to first page when filters change
    }, [diamonds, filters, hideInquiryDiamonds, dashboardFilter]);
    // Calculate statistics (only count diamonds with prices for averages)
    const stats = useMemo(() => {
        const pricedDiamonds = filteredDiamonds.filter(d => (d.priceUSD || d.totalPrice || 0) > 0);
        const totalValue = pricedDiamonds.reduce((sum, d) => sum + (d.priceUSD || d.totalPrice || 0), 0);
        const avgPrice = pricedDiamonds.length > 0 ? totalValue / pricedDiamonds.length : 0;
        const avgCarat = pricedDiamonds.length > 0
            ? pricedDiamonds.reduce((sum, d) => sum + d.caratWeight, 0) / pricedDiamonds.length
            : 0;
        return {
            total: filteredDiamonds.length,
            totalPriced: pricedDiamonds.length,
            totalValue,
            avgPrice,
            avgCarat,
            certified: filteredDiamonds.filter(d => d.certified).length,
            featured: filteredDiamonds.filter(d => d.featured).length
        };
    }, [filteredDiamonds]);
    // Sort diamonds
    const sortedDiamonds = useMemo(() => {
        if (!sortKey || !sortDirection)
            return filteredDiamonds;
        return [...filteredDiamonds].sort((a, b) => {
            let aVal;
            let bVal;
            switch (sortKey) {
                case 'stockNumber':
                    aVal = a.stockNumber || a.id || '';
                    bVal = b.stockNumber || b.id || '';
                    break;
                case 'shape':
                    aVal = a.shape || '';
                    bVal = b.shape || '';
                    break;
                case 'carat':
                    aVal = a.caratWeight || 0;
                    bVal = b.caratWeight || 0;
                    break;
                case 'color':
                    aVal = a.color || '';
                    bVal = b.color || '';
                    break;
                case 'clarity':
                    aVal = a.clarity || '';
                    bVal = b.clarity || '';
                    break;
                case 'cut':
                    aVal = a.cut || '';
                    bVal = b.cut || '';
                    break;
                case 'lab':
                    aVal = a.lab || '';
                    bVal = b.lab || '';
                    break;
                case 'pricePerCarat':
                    aVal = a.pricePerCarat || a.priceUSD || 0;
                    bVal = b.pricePerCarat || b.priceUSD || 0;
                    break;
                case 'total':
                    aVal = a.totalPrice || a.priceUSD || 0;
                    bVal = b.totalPrice || b.priceUSD || 0;
                    break;
                case 'status':
                    aVal = a.status || '';
                    bVal = b.status || '';
                    break;
                default:
                    return 0;
            }
            if (typeof aVal === 'string') {
                const comparison = aVal.localeCompare(bVal);
                return sortDirection === 'asc' ? comparison : -comparison;
            }
            return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [filteredDiamonds, sortKey, sortDirection]);
    // Pagination computed values
    const totalPages = Math.max(1, Math.ceil(sortedDiamonds.length / PAGE_SIZE));
    const paginatedDiamonds = sortedDiamonds.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };
    const clearFilters = () => {
        setFilters({
            shape: '',
            color: '',
            clarity: '',
            cut: '',
            lab: '',
            minCarat: '',
            maxCarat: '',
            minPrice: '',
            maxPrice: '',
            status: '',
            search: ''
        });
        // Also clear dashboard filter
        setDashboardFilter(null);
        if (onClearFilters) {
            onClearFilters();
        }
    };
    const clearDashboardFilter = () => {
        setDashboardFilter(null);
        if (onClearFilters) {
            onClearFilters();
        }
    };
    // Get label for active dashboard filter
    const getDashboardFilterLabel = () => {
        if (!dashboardFilter?.filterType)
            return null;
        switch (dashboardFilter.filterType) {
            case 'slow-moving': return 'Slow-Moving Inventory (90+ Days)';
            case 'high-value': return 'High-Value Items ($50K+)';
            case 'low-margin': return 'Low-Margin Items (<20%)';
            case 'missing-cert': return 'Missing Certificates';
            default: return null;
        }
    };
    const handleExportCSV = () => {
        const preparedData = prepareDiamondsForExport(filteredDiamonds, false);
        const success = exportToCSV(preparedData, 'diamonds-inventory');
        if (success) {
            activityTracker.trackAdminAction('exported-data', `Exported ${filteredDiamonds.length} diamonds to CSV`, { format: 'csv', count: filteredDiamonds.length });
            notify.success('Export Complete', `Successfully exported ${filteredDiamonds.length} diamonds to CSV`);
        }
        else {
            notify.error('Export Failed', 'CSV export failed. Please try again.');
        }
    };
    const handleExportJSON = () => {
        const preparedData = prepareDiamondsForExport(filteredDiamonds, true);
        const exportData = {
            exported: new Date().toISOString(),
            filters: filters,
            count: filteredDiamonds.length,
            totalValue: stats.totalValue,
            statistics: {
                avgPrice: stats.avgPrice,
                avgCarat: stats.avgCarat,
                featuredCount: stats.featured,
                certifiedCount: stats.certified
            },
            diamonds: preparedData
        };
        const success = exportToJSON([exportData], 'diamonds-inventory');
        if (success) {
            activityTracker.trackAdminAction('exported-data', `Exported ${filteredDiamonds.length} diamonds to JSON`, { format: 'json', count: filteredDiamonds.length });
            notify.success('Export Complete', `Successfully exported ${filteredDiamonds.length} diamonds to JSON`);
        }
        else {
            notify.error('Export Failed', 'JSON export failed. Please try again.');
        }
    };
    // Legacy function for backward compatibility
    const exportFiltered = handleExportJSON;
    // Get unique values for filter dropdowns
    const uniqueShapes = [...new Set(diamonds.map(d => d.shape))].filter(Boolean).sort();
    const uniqueColors = [...new Set(diamonds.map(d => d.color))].filter(Boolean).sort();
    const uniqueClarities = [...new Set(diamonds.map(d => d.clarity))].filter(Boolean).sort();
    const uniqueCuts = [...new Set(diamonds.map(d => d.cut))].filter(Boolean).sort();
    const uniqueLabs = [...new Set(diamonds.map(d => d.lab))].filter(Boolean).sort();
    // Smart filtering: Get available diamonds based on current filters (excluding the filter being checked)
    const getAvailableDiamondsForFilter = (excludeFilter) => {
        let available = diamonds;
        // Apply all filters except the one we're checking
        if (excludeFilter !== 'shape' && filters.shape) {
            available = available.filter(d => d.shape.toLowerCase() === filters.shape.toLowerCase());
        }
        if (excludeFilter !== 'color' && filters.color) {
            available = available.filter(d => d.color === filters.color);
        }
        if (excludeFilter !== 'clarity' && filters.clarity) {
            available = available.filter(d => d.clarity === filters.clarity);
        }
        if (excludeFilter !== 'cut' && filters.cut) {
            available = available.filter(d => d.cut === filters.cut);
        }
        if (excludeFilter !== 'lab' && filters.lab) {
            available = available.filter(d => d.lab === filters.lab);
        }
        // Apply search filter
        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            available = available.filter(d => d.shape.toLowerCase().includes(searchTerm) ||
                d.color.toLowerCase().includes(searchTerm) ||
                d.clarity.toLowerCase().includes(searchTerm) ||
                d.lab.toLowerCase().includes(searchTerm) ||
                d.id.toLowerCase().includes(searchTerm));
        }
        // Apply carat range
        if (filters.minCarat) {
            const minCarat = parseFloat(filters.minCarat);
            available = available.filter(d => d.caratWeight >= minCarat);
        }
        if (filters.maxCarat) {
            const maxCarat = parseFloat(filters.maxCarat);
            available = available.filter(d => d.caratWeight <= maxCarat);
        }
        // Apply price range
        if (filters.minPrice) {
            const minPrice = parseFloat(filters.minPrice);
            available = available.filter(d => (d.priceUSD || d.totalPrice || 0) >= minPrice);
        }
        if (filters.maxPrice) {
            const maxPrice = parseFloat(filters.maxPrice);
            available = available.filter(d => (d.priceUSD || d.totalPrice || 0) <= maxPrice);
        }
        // Apply status filter
        if (filters.status) {
            available = available.filter(d => d.status === filters.status);
        }
        return available;
    };
    // Smart count helpers - only count diamonds that would be available with current filters
    const getShapeCount = (shape) => {
        const available = getAvailableDiamondsForFilter('shape');
        return available.filter(d => d.shape.toLowerCase() === shape.toLowerCase()).length;
    };
    // Available shape images (must match files in /public/diamondshape/)
    const availableShapeImages = [
        'round', 'oval', 'princess', 'emerald', 'asscher', 'cushion',
        'marquise', 'radiant', 'pear', 'heart'
    ];
    // Available lab seal images (must match files in /public/seal/)
    const availableLabSeals = [
        'gia', 'ags', 'egi', 'hrd', 'igi'
    ];
    // Smart count helpers - only count diamonds that would be available with current filters
    const getColorCount = (color) => {
        const available = getAvailableDiamondsForFilter('color');
        return available.filter(d => d.color === color).length;
    };
    const getClarityCount = (clarity) => {
        const available = getAvailableDiamondsForFilter('clarity');
        return available.filter(d => d.clarity === clarity).length;
    };
    const getCutCount = (cut) => {
        const available = getAvailableDiamondsForFilter('cut');
        return available.filter(d => d.cut === cut).length;
    };
    const getLabCount = (lab) => {
        const available = getAvailableDiamondsForFilter('lab');
        return available.filter(d => d.lab === lab).length;
    };
    // Format numbers consistently to prevent layout shifts
    const formatNumber = (num, minWidth = 6) => {
        const formatted = num.toLocaleString();
        return formatted.padStart(minWidth, ' ');
    };
    const formatCurrency = (num, minWidth = 8) => {
        const formatted = `$${num.toLocaleString()}`;
        return formatted.padStart(minWidth, ' ');
    };
    const formatDecimal = (num, decimals = 2, minWidth = 6) => {
        const formatted = num.toFixed(decimals);
        return formatted.padStart(minWidth, ' ');
    };
    // Clarity quality mapping
    const clarityQuality = {
        'FL': 10, 'IF': 9, 'VVS1': 8, 'VVS2': 7, 'VS1': 6, 'VS2': 5, 'SI1': 4, 'SI2': 3, 'I1': 2, 'I2': 1, 'I3': 0
    };
    // Cut quality mapping
    const cutQuality = {
        'Excellent': 5, 'Very Good': 4, 'Good': 3, 'Fair': 2, 'Poor': 1
    };
    if (isLoading) {
        return (_jsx(PageContainer, { children: _jsx(PageTitle, { children: "Loading Diamond Inventory..." }) }));
    }
    return (_jsxs(PageContainer, { children: [_jsx(PageTitle, { children: "Diamond Inventory Management" }), _jsx(Text, { children: "Comprehensive diamond inventory with advanced filtering and management tools" }), dashboardFilter && getDashboardFilterLabel() && (_jsxs(ActiveFilterBanner, { children: [_jsxs("div", { className: "filter-info", children: [_jsx("span", { className: "label", children: "Active Filter:" }), _jsx("span", { className: "value", children: getDashboardFilterLabel() })] }), _jsx("button", { className: "clear-btn", onClick: clearDashboardFilter, children: "Clear Filter" })] })), _jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: stats.total }), _jsx("div", { className: "stat-label", children: "Total Diamonds" }), _jsx("div", { className: "stat-sublabel", children: "In current view" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: ["$", stats.totalValue.toLocaleString()] }), _jsx("div", { className: "stat-label", children: "Total Value" }), _jsxs("div", { className: "stat-sublabel", children: [stats.totalPriced, " priced diamonds"] })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: ["$", Math.round(stats.avgPrice).toLocaleString()] }), _jsx("div", { className: "stat-label", children: "Average Price" }), _jsxs("div", { className: "stat-sublabel", children: [stats.avgCarat.toFixed(2), "ct avg (priced only)"] })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: stats.certified }), _jsx("div", { className: "stat-label", children: "Certified" }), _jsxs("div", { className: "stat-sublabel", children: [stats.featured, " featured"] })] })] }), _jsxs(FilterSection, { children: [_jsx(FilterHeader, { children: _jsxs(FilterTitle, { children: [_jsx(FiSliders, {}), " Advanced Diamond Search"] }) }), _jsx(SearchBar, { children: _jsx(SearchInput, { id: "search", type: "text", placeholder: "Search by ID, shape, color, clarity, stock number, or certificate...", value: filters.search, onChange: (e) => handleFilterChange('search', e.target.value) }) }), _jsxs(FilterMainGrid, { children: [_jsx(FilterFullWidth, { children: _jsx(ShapeFilter, { selectedShape: filters.shape, shapes: uniqueShapes, onShapeChange: (shape) => handleFilterChange('shape', shape), getShapeCount: getShapeCount }) }), _jsxs(FilterPairRow, { children: [_jsx(ColorFilter, { selectedColor: filters.color, colors: uniqueColors, onColorChange: (color) => handleFilterChange('color', color), getColorCount: getColorCount }), _jsx(CutQualityFilter, { selectedCut: filters.cut, cuts: uniqueCuts, onCutChange: (cut) => handleFilterChange('cut', cut), getCutCount: getCutCount, cutQuality: cutQuality })] }), _jsxs(FilterPairRow, { children: [_jsx(ClarityFilter, { selectedClarity: filters.clarity, clarities: uniqueClarities, onClarityChange: (clarity) => handleFilterChange('clarity', clarity), getClarityCount: getClarityCount, clarityQuality: clarityQuality }), _jsx(LabFilter, { selectedLab: filters.lab, labs: uniqueLabs, onLabChange: (lab) => handleFilterChange('lab', lab), getLabCount: getLabCount })] }), _jsxs(FilterFullWidth, { children: [_jsx(FilterLabel, { children: "Size & Price Ranges" }), _jsxs(RangeGrid, { children: [_jsxs(RangeInputGroup, { children: [_jsx("label", { htmlFor: "minCarat", children: "Min Carat" }), _jsx(RangeInput, { id: "minCarat", type: "number", step: "0.01", placeholder: "0.00", value: filters.minCarat, onChange: (e) => handleFilterChange('minCarat', e.target.value) })] }), _jsxs(RangeInputGroup, { children: [_jsx("label", { htmlFor: "maxCarat", children: "Max Carat" }), _jsx(RangeInput, { id: "maxCarat", type: "number", step: "0.01", placeholder: "10.00", value: filters.maxCarat, onChange: (e) => handleFilterChange('maxCarat', e.target.value) })] }), _jsxs(RangeInputGroup, { children: [_jsx("label", { htmlFor: "minPrice", children: "Min Price ($)" }), _jsx(RangeInput, { id: "minPrice", type: "number", placeholder: "0", value: filters.minPrice, onChange: (e) => handleFilterChange('minPrice', e.target.value) })] }), _jsxs(RangeInputGroup, { children: [_jsx("label", { htmlFor: "maxPrice", children: "Max Price ($)" }), _jsx(RangeInput, { id: "maxPrice", type: "number", placeholder: "1000000", value: filters.maxPrice, onChange: (e) => handleFilterChange('maxPrice', e.target.value) })] })] })] }), _jsxs(FilterFullWidth, { children: [_jsx(FilterLabel, { children: "Status" }), _jsxs(Select, { id: "status", value: filters.status, onChange: (e) => handleFilterChange('status', e.target.value), style: { maxWidth: '300px' }, children: [_jsx("option", { value: "", children: "All Status" }), _jsx("option", { value: "available", children: "Available" }), _jsx("option", { value: "sold", children: "Sold" }), _jsx("option", { value: "reserved", children: "Reserved" })] })] })] }), _jsxs(FilterActions, { children: [_jsx(ClearButton, { onClick: clearFilters, children: "Clear All Filters" }), _jsxs("div", { style: { display: 'flex', gap: '12px' }, children: [_jsxs(ExportButton, { onClick: handleExportCSV, children: [_jsx(FiDownload, {}), " Export CSV (", filteredDiamonds.length, ")"] }), _jsxs(ExportButton, { onClick: handleExportJSON, children: [_jsx(FiDownload, {}), " Export JSON"] })] })] })] }), _jsxs(InquiryToggleBar, { children: [_jsxs(ToggleInfo, { children: [_jsx("div", { className: "toggle-title", children: "Inquiry Diamonds Filter" }), _jsxs("div", { className: "toggle-description", children: ["Diamonds with status \"inquiry\" have no listed price (price = $0).", hideInquiryDiamonds
                                        ? ` Currently hidden to show only priced inventory.`
                                        : ` Currently showing all diamonds including inquiry items.`] }), _jsx("div", { className: "toggle-count", children: hideInquiryDiamonds
                                    ? `${diamonds.filter(d => d.status === 'inquiry').length} inquiry diamonds hidden`
                                    : `Showing all ${diamonds.length} diamonds (${diamonds.filter(d => d.status === 'inquiry').length} inquiry + ${diamonds.filter(d => d.status === 'available').length} priced)` })] }), _jsx(ToggleButton, { "$active": !hideInquiryDiamonds, onClick: () => setHideInquiryDiamonds(!hideInquiryDiamonds), children: hideInquiryDiamonds ? 'Show Inquiry Diamonds' : 'Hide Inquiry Diamonds' })] }), _jsxs(InventoryContainer, { ref: inventoryContainerRef, children: [_jsxs(InventoryHeader, { children: [_jsxs(InventoryTitleGroup, { children: [_jsx(InventoryIcon, { children: _jsx(GiCutDiamond, {}) }), _jsxs(TitleText, { children: [_jsxs(InventoryTitle, { children: ["Diamond ", _jsx("span", { children: "Inventory" })] }), _jsx(InventorySubtitle, { children: "Showing filtered results from your collection" })] })] }), _jsxs(InventoryStats, { children: [_jsxs(StatPill, { "$delay": 200, children: [_jsx(StatPillValue, { children: filteredDiamonds.length }), _jsx(StatPillLabel, { children: "Stones" })] }), _jsxs(StatPill, { "$delay": 250, children: [_jsxs(StatPillValue, { children: ["$", (stats.totalValue / 1000).toFixed(0), "K"] }), _jsx(StatPillLabel, { children: "Value" })] }), _jsxs(StatPill, { "$delay": 300, children: [_jsx(StatPillValue, { children: stats.avgCarat.toFixed(2) }), _jsx(StatPillLabel, { children: "Avg CT" })] }), _jsxs(StatPill, { "$delay": 350, children: [_jsx(StatPillValue, { children: stats.certified }), _jsx(StatPillLabel, { children: "Certified" })] })] }), _jsxs(ViewToggle, { children: [_jsx(ViewBtn, { "$active": true, title: "List View", children: _jsx(FiList, {}) }), _jsx(ViewBtn, { title: "Grid View", children: _jsx(FiGrid, {}) })] })] }), _jsx(InventoryTableWrapper, { children: _jsxs(InventoryTable, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Image" }), _jsx(SortableTh, { sortKey: "stockNumber", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Stock #" }), _jsx(SortableTh, { sortKey: "shape", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Shape" }), _jsx(SortableTh, { sortKey: "carat", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Carat" }), _jsx(SortableTh, { sortKey: "color", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Color" }), _jsx(SortableTh, { sortKey: "clarity", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Clarity" }), _jsx(SortableTh, { sortKey: "cut", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Cut" }), _jsx(SortableTh, { sortKey: "lab", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Lab" }), _jsx("th", { children: "Cert #" }), _jsx(SortableTh, { sortKey: "pricePerCarat", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Price/CT" }), _jsx(SortableTh, { sortKey: "total", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Total" }), _jsx(SortableTh, { sortKey: "status", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Status" }), _jsx("th", { children: "Actions" })] }) }), _jsx("tbody", { children: paginatedDiamonds.map(diamond => (_jsxs("tr", { children: [_jsx("td", { children: _jsx(DiamondThumb, { src: getDiamondShapeImage(diamond.shape, diamond.shapeCode), alt: `${diamond.shape} diamond`, onError: (e) => {
                                                        e.currentTarget.style.display = 'none';
                                                    } }) }), _jsx("td", { children: _jsx(StockNumber, { children: diamond.stockNumber || diamond.id }) }), _jsx("td", { children: _jsx(ShapeBadge, { children: diamond.shape.toUpperCase() }) }), _jsx("td", { children: _jsxs(CaratValue, { children: [diamond.caratWeight, "ct"] }) }), _jsx(GradeCell, { children: diamond.color }), _jsx(GradeCell, { children: diamond.clarity }), _jsx("td", { children: diamond.cut || '—' }), _jsx("td", { children: diamond.certified ? (_jsx(LabBadge, { "$lab": diamond.lab, children: diamond.lab })) : (_jsx("span", { style: { color: 'var(--text-muted)' }, children: "\u2014" })) }), _jsx("td", { children: _jsx(CertNumber, { children: diamond.certificateNumber || '—' }) }), _jsx("td", { children: _jsx(PriceValue, { children: diamond.pricePerCarat
                                                        ? `$${diamond.pricePerCarat.toLocaleString()}`
                                                        : (diamond.priceUSD ? `$${diamond.priceUSD.toLocaleString()}` : '—') }) }), _jsxs("td", { children: [_jsx(PriceTotal, { children: diamond.priceDisplay ||
                                                            (diamond.totalPrice ? `$${diamond.totalPrice.toLocaleString()}` :
                                                                diamond.priceUSD ? `$${diamond.priceUSD.toLocaleString()}` : 'Inquiry') }), diamond.featured && (_jsx(FeaturedBadge, { children: "\u2605" }))] }), _jsx("td", { children: _jsxs(StatusIndicator, { "$status": diamond.status, children: [_jsx(StatusDot, {}), diamond.status.toUpperCase()] }) }), _jsxs(ActionsCell, { children: [_jsx(ActionBtn, { onClick: () => setSelectedDiamond(diamond), children: "View" }), diamond.certificateNumber && diamond.lab && getCertificateUrl(diamond.lab, diamond.certificateNumber) && (_jsx(ActionBtn, { onClick: () => {
                                                            if (diamond.lab && diamond.certificateNumber) {
                                                                const certUrl = getCertificateUrl(diamond.lab, diamond.certificateNumber);
                                                                if (certUrl) {
                                                                    window.open(certUrl, '_blank', 'noopener,noreferrer');
                                                                }
                                                            }
                                                        }, title: `View ${diamond.lab} Certificate #${diamond.certificateNumber}`, children: "Cert" })), _jsx(ActionBtn, { "$primary": true, onClick: () => setSelectedDiamond(diamond), children: "Edit" }), diamond.status === 'available' && (_jsx(ActionBtn, { onClick: () => setAuctionDiamond(diamond), title: "Create Auction", children: _jsx(FaGavel, { size: 11 }) }))] })] }, diamond.id))) })] }) }), _jsxs(InventoryFooter, { children: [_jsxs(Pagination, { children: [_jsxs(PaginationBtn, { disabled: currentPage === 1, onClick: () => setCurrentPage(p => Math.max(1, p - 1)), children: [_jsx(FiChevronLeft, {}), "Prev"] }), _jsxs(PaginationInfo, { children: ["Page ", _jsx("strong", { children: currentPage }), " of ", _jsx("strong", { children: totalPages })] }), _jsxs(PaginationBtn, { disabled: currentPage >= totalPages, onClick: () => setCurrentPage(p => Math.min(totalPages, p + 1)), children: ["Next", _jsx(FiChevronRight, {})] })] }), _jsxs(ShowingInfo, { children: ["Showing ", _jsxs("strong", { children: [((currentPage - 1) * PAGE_SIZE) + 1, "-", Math.min(currentPage * PAGE_SIZE, sortedDiamonds.length)] }), " of ", _jsx("strong", { children: sortedDiamonds.length }), " stones"] })] }), filteredDiamonds.length === 0 && (_jsx("div", { style: { textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }, children: "No diamonds match your current filters." }))] }), selectedDiamond && (_jsx(DiamondDetailModal, { diamond: selectedDiamond, onClose: () => setSelectedDiamond(null), onCreateAuction: () => { setAuctionDiamond(selectedDiamond); setSelectedDiamond(null); } })), auctionDiamond && (_jsx(CreateAuctionModal, { diamond: auctionDiamond, onClose: () => setAuctionDiamond(null), onCreated: () => setAuctionDiamond(null) }))] }));
};
export default DiamondInventory;
